import React, { useRef, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { PerspectiveCamera, Html, RoundedBox } from "@react-three/drei";
import { useSpring, animated } from "@react-spring/three";
import * as THREE from "three";

/**
 * Configuration des étapes : 8 étapes principales (4 layers × 2 phases)
 * Chaque layer apparaît entièrement, puis certains cubes changent de couleur
 */
const STEP_CONFIG = [
  // Étape 0 : Layer 0 - 4 cubes gris clair (Partial Range of Field Enhance)
  {
    layer: 0,
    colors: {
      "0-0": "#d4d9db", "0-1": "#d4d9db",
      "1-0": "#d4d9db", "1-1": "#d4d9db",
    }
  },
  // Étape 1 : Layer 0 - 2 cubes Refractive deviennent gris foncé (Enhanced Monofocal/Monofocal+)
  {
    layer: 0,
    colors: {
      "0-0": "#d4d9db", "0-1": "#626262",
      "1-0": "#d4d9db", "1-1": "#626262",
    }
  },

  // Étape 2 : Layer 1 - 4 cubes gris clair (Partial Range of Field Extended)
  {
    layer: 1,
    colors: {
      "0-0": "#d4d9db", "0-1": "#d4d9db",
      "1-0": "#d4d9db", "1-1": "#d4d9db",
    }
  },
  // Étape 3 : Layer 1 - 3 cubes deviennent bleu (Extended Range EDOF like)
  {
    layer: 1,
    colors: {
      "0-0": "#d4d9db", "0-1": "#a7c6e0",
      "1-0": "#a7c6e0", "1-1": "#a7c6e0",
    }
  },

  // Étape 4 : Layer 2 - 4 cubes gris clair (Full Range of Field Continuous)
  {
    layer: 2,
    colors: {
      "0-0": "#d4d9db", "0-1": "#d4d9db",
      "1-0": "#d4d9db", "1-1": "#d4d9db",
    }
  },
  // Étape 5 : Layer 2 - 2 cubes Premium deviennent beige/orange (Extended Range EDOF like)
  {
    layer: 2,
    colors: {
      "0-0": "#f8dccd", "0-1": "#f8dccd",
      "1-0": "#d4d9db", "1-1": "#f8dccd",
    }
  },

  // Étape 6 : Layer 3 - 4 cubes gris clair (Full Range of Field Smooth)
  {
    layer: 3,
    colors: {
      "0-0": "#d4d9db", "0-1": "#d4d9db",
      "1-0": "#d4d9db", "1-1": "#d4d9db",
    }
  },
  // Étape 7 : Layer 3 - 2 cubes Premium deviennent rose/violet (Trifocal / Multifocal)
  {
    layer: 3,
    colors: {
      "0-0": "#f3d0ee", "0-1": "#d4d9db",
      "1-0": "#f3d0ee", "1-1": "#d4d9db",
    }
  },
];

// Mapping des produits vers leurs colonnes spécifiques
// rowIdx: 0 = Competitive, 1 = Premium
// colIdx: 0 = Diffractive, 1 = Refractive
const PRODUCT_COLUMN_MAP = {
  0: { row: 0, col: 1 }, // Lucidis: Refractive / Competitive
  1: { row: 0.6, col: 0.5 }, // Eden: Centre (entre diffractive et refractive, légèrement vers premium)
  2: { row: 1, col: 1 }, // New Product: Refractive / Premium
};

// Mapping des colonnes vers leurs angles de rotation
const COLUMN_ROTATION_MAP = {
  "0-0": Math.PI / 6,      // Diffractive / Competitive
  "0-1": -Math.PI / 4.5,   // Refractive / Competitive
  "1-0": Math.PI / 6,      // Diffractive / Premium (même angle que 0-0)
  "1-1": -Math.PI / 2.5,   // Refractive / Premium
};

// Mapping des produits vers leurs angles de rotation optimaux
const PRODUCT_ROTATION_MAP = {
  0: -Math.PI / 4.5,  // Lucidis: Refractive / Competitive
  1: Math.PI / 6,      // Eden: Diffractive / Premium
  2: -Math.PI / 2.5,   // New Product: Refractive / Premium
};

/**
 * Label d'axe cliquable avec style interactif
 */
const AxisLabel = ({ text, position, isSelected, onClick }) => (
  <Html position={position} center transform={true}>
    <div
      onClick={onClick}
      style={{
        fontSize: "13px",
        fontWeight: 700,
        color: "#333",
        background: "rgba(255, 255, 255, 0.95)",
        padding: "6px 14px",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "all 0.3s",
        border: isSelected ? "2px solid #C41230" : "2px solid rgba(0,0,0,0.15)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(196, 18, 48, 0.1)";
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
      }}
    >
      {text}
    </div>
  </Html>
);

/**
 * Cube 3D empilé avec highlight produit
 */
function StackedCube({ position, color, isVisible, delay = 0, isHighlighted = false, highlightColor = null, tooltipContent = null, tooltipLink = null, showTooltipAlways = false, isMobile = false }) {
  const meshRef = useRef();
  const [isHovered, setIsHovered] = React.useState(false);
  const [spring, springApi] = useSpring(() => ({
    posY: isVisible ? 0 : -3,
    opacity: isVisible ? 1 : 0,
    config: { tension: 110, friction: 20 },
  }));

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        springApi.start({ posY: 0, opacity: 1 });
      }, delay);
    } else {
      springApi.start({ posY: -3, opacity: 0 });
    }
  }, [isVisible, delay, springApi]);

  // Couleur dynamique basée sur highlight
  const displayColor = isHighlighted && highlightColor ? highlightColor : color;

  const cubeSize = 3.4;
  const cubeHeight = 0.55;
  const cornerRadius = 0.12;

  // Afficher le tooltip si showTooltipAlways est vrai OU si on survole
  const shouldShowTooltip = tooltipContent && (showTooltipAlways || isHovered);

  return (
    <animated.group
      ref={meshRef}
      position={spring.posY.to((y) => [position[0], y + position[1], position[2]])}
    >
      {/* Cube arrondi - style verre dépoli */}
      <RoundedBox
        args={[cubeSize, cubeHeight, cubeSize]}
        radius={cornerRadius}
        smoothness={4}
        position={[0, cubeHeight / 2, 0]}
        castShadow
        receiveShadow
        onPointerOver={(e) => {
          e.stopPropagation();
          if (tooltipContent && !showTooltipAlways) setIsHovered(true);
          document.body.style.cursor = tooltipContent ? 'pointer' : 'default';
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setIsHovered(false);
          document.body.style.cursor = 'default';
        }}
      >
        <animated.meshPhysicalMaterial
          color={displayColor}
          roughness={0.15}
          metalness={0.35}
          opacity={spring.opacity.to(o => o * 0.95)}
          transparent
          transmission={0.05}
          thickness={0.5}
          clearcoat={0.7}
          clearcoatRoughness={0.2}
          emissive={isHighlighted ? displayColor : "#000000"}
          emissiveIntensity={isHighlighted ? 0.7 : 0}
          envMapIntensity={1.6}
        />
      </RoundedBox>

      {/* Point light glow si highlighted */}
      {isHighlighted && (
        <pointLight
          position={[0, cubeHeight + 0.3, 0]}
          color={displayColor}
          intensity={1.5}
          distance={4.5}
          decay={1.8}
        />
      )}

      {/* Tooltip - affiché automatiquement si showTooltipAlways est vrai */}
      {shouldShowTooltip && (
        <Html 
          position={[0, cubeHeight + (isMobile ? 1 : 1.5), 0]} 
          center
          style={{
            pointerEvents: tooltipLink ? 'auto' : 'none',
          }}
        >
          <div style={{
            background: `${displayColor}f2`,
            color: '#ffffff',
            padding: isMobile ? '6px 8px' : '8px 12px',
            borderRadius: '6px',
            fontSize: isMobile ? '9px' : '11px',
            fontWeight: '600',
            lineHeight: '1.3',
            whiteSpace: 'normal',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            border: '2px solid rgba(255,255,255,0.2)',
            pointerEvents: tooltipLink ? 'auto' : 'none',
            maxWidth: isMobile ? '140px' : '200px',
            textAlign: 'center',
          }}>
            {tooltipContent.split('\n').map((line, i) => {
              const isLastLine = i === tooltipContent.split('\n').length - 1;
              const isDiscoverMore = line.toLowerCase().includes('discover more');

              if (isLastLine && isDiscoverMore && tooltipLink) {
                return (
                  <div
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = tooltipLink;
                    }}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      marginTop: '4px',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    {line}
                  </div>
                );
              }
              return <div key={i}>{line}</div>;
            })}
          </div>
        </Html>
      )}
    </animated.group>
  );
}

/**
 * Croix 3D pour représenter un produit (Eden) - Version pointillés
 */
function ProductCross({ position, color, isVisible, delay = 0, tooltipContent = null, showTooltipAlways = false, isMobile = false }) {
  const meshRef = useRef();
  const [isHovered, setIsHovered] = React.useState(false);
  const [spring, springApi] = useSpring(() => ({
    posY: isVisible ? 0 : -3,
    opacity: isVisible ? 1 : 0,
    scale: isVisible ? 1 : 0,
    config: { tension: 110, friction: 20 },
  }));

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        springApi.start({ posY: 0, opacity: 1, scale: 1 });
      }, delay);
    } else {
      springApi.start({ posY: -3, opacity: 0, scale: 0 });
    }
  }, [isVisible, delay, springApi]);

  // Dimensions réduites pour une croix plus petite
  const crossThickness = 0.03;
  const crossLength = 0.6;
  const crossHeight = 0.6;
  const dashCount = 5;
  const dashLength = 0.08;
  const gapLength = 0.04;

  // Afficher le tooltip si showTooltipAlways est vrai OU si on survole
  const shouldShowTooltip = tooltipContent && (showTooltipAlways || isHovered);

  return (
    <animated.group
      ref={meshRef}
      position={spring.posY.to((y) => [position[0], y + position[1], position[2]])}
      scale={spring.scale}
      rotation={[0, Math.PI / 2, 0]}
    >
      {/* Barre verticale de la croix - en pointillés */}
      {Array.from({ length: dashCount }).map((_, i) => {
        const offset = (i - (dashCount - 1) / 2) * (dashLength + gapLength);
        return (
          <mesh
            key={`v-dash-${i}`}
            position={[0, crossHeight * 0.7 + offset, 0]}
            onPointerOver={(e) => {
              e.stopPropagation();
              if (tooltipContent && !showTooltipAlways) setIsHovered(true);
              document.body.style.cursor = tooltipContent ? 'pointer' : 'default';
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              setIsHovered(false);
              document.body.style.cursor = 'default';
            }}
          >
            <boxGeometry args={[crossThickness, dashLength, crossThickness]} />
            <meshPhysicalMaterial
              color={color}
              roughness={0.2}
              metalness={0.6}
              emissive={color}
              emissiveIntensity={0.5}
            />
          </mesh>
        );
      })}

      {/* Barre horizontale de la croix - en pointillés */}
      {Array.from({ length: dashCount }).map((_, i) => {
        const offset = (i - (dashCount - 1) / 2) * (dashLength + gapLength);
        return (
          <mesh
            key={`h-dash-${i}`}
            position={[offset, crossHeight * 0.7, 0]}
            onPointerOver={(e) => {
              e.stopPropagation();
              if (tooltipContent && !showTooltipAlways) setIsHovered(true);
              document.body.style.cursor = tooltipContent ? 'pointer' : 'default';
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              setIsHovered(false);
              document.body.style.cursor = 'default';
            }}
          >
            <boxGeometry args={[dashLength, crossThickness, crossThickness]} />
            <meshPhysicalMaterial
              color={color}
              roughness={0.2}
              metalness={0.6}
              emissive={color}
              emissiveIntensity={0.5}
            />
          </mesh>
        );
      })}

      {/* Point light glow réduit */}
      <pointLight
        position={[0, crossHeight * 0.7, 0]}
        color={color}
        intensity={1.2}
        distance={3}
        decay={1.8}
      />

      {/* Tooltip - affiché automatiquement si showTooltipAlways est vrai */}
      {shouldShowTooltip && (
        <Html 
          position={[0, crossHeight + (isMobile ? 0.8 : 1.2), 0]} 
          center
          style={{
            pointerEvents: 'none',
          }}
        >
          <div style={{
            background: 'rgba(17, 69, 141, 0.95)',
            color: '#ffffff',
            padding: isMobile ? '6px 8px' : '8px 12px',
            borderRadius: '6px',
            fontSize: isMobile ? '9px' : '11px',
            fontWeight: '600',
            lineHeight: '1.3',
            whiteSpace: 'normal',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            border: '2px solid rgba(255,255,255,0.2)',
            pointerEvents: 'none',
            maxWidth: isMobile ? '140px' : '200px',
            textAlign: 'center',
          }}>
            {tooltipContent.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </Html>
      )}
    </animated.group>
  );
}

/**
 * Scène 3D - Grille 2×2 avec 5 couches empilées
 */
function Comparatif3D({
  gridData = [],
  stepIdx = 0,
  products = [],
  selectedProduct = null,
  selectedColumn = null,
  onColumnSelect = () => { },
  isMobile = false,
}) {
  const groupRef = useRef();

  useFrame(() => {
    if (groupRef.current) {
      let targetRotY = -Math.PI / 6; // Rotation de base légèrement ajustée pour meilleure vue

      // Ajustement de rotation selon la colonne sélectionnée
      if (selectedColumn !== null) {
        targetRotY = COLUMN_ROTATION_MAP[selectedColumn] || targetRotY;
      }
      // Ajustement de rotation selon le produit sélectionné
      else if (selectedProduct !== null) {
        targetRotY = PRODUCT_ROTATION_MAP[selectedProduct] || targetRotY;
      } else {
        // Rotation progressive par étape quand aucun produit n'est sélectionné
        targetRotY = -0.52 + stepIdx * 0.02;
      }

      groupRef.current.rotation.y += (targetRotY - groupRef.current.rotation.y) * 0.09;
    }
  });

  // Obtenir toutes les étapes jusqu'à l'étape courante pour accumuler les layers
  const getAccumulatedSteps = () => {
    const accumulated = {};
    for (let i = 0; i <= Math.min(stepIdx, STEP_CONFIG.length - 1); i++) {
      const step = STEP_CONFIG[i];
      if (!accumulated[step.layer]) {
        accumulated[step.layer] = step;
      } else {
        // Mettre à jour avec la dernière configuration de ce layer
        accumulated[step.layer] = step;
      }
    }
    return accumulated;
  };

  // Vérifier si un layer entier est visible
  const isLayerVisible = (layerIdx) => {
    const accumulatedSteps = getAccumulatedSteps();
    return accumulatedSteps[layerIdx] !== undefined;
  };

  // Obtenir la couleur d'un cube selon l'étape courante
  const getCubeColor = (layerIdx, rowIdx, colIdx) => {
    const accumulatedSteps = getAccumulatedSteps();
    const stepConfig = accumulatedSteps[layerIdx];

    if (!stepConfig) return "#a0a0a0";

    const colorKey = `${rowIdx}-${colIdx}`;
    return stepConfig.colors[colorKey] || "#a0a0a0";
  };

  // Espacements - Grille 2×2 bien répartie
  const cubeSpacing = 4;  // Plus d'espace entre cubes pour meilleure lisibilité
  const layerHeight = 0.8;  // Plus d'espace vertical entre layers
  const gridSize = 2; // 2×2
  const gridOffset = (gridSize - 1) * cubeSpacing / 2;

  // Paramètres de caméra adaptés au mobile
  const cameraPosition = isMobile ? [0, 13, 21] : [0, 11.5, 17];
  const cameraFov = isMobile ? 55 : 45;
  const cameraRotation = isMobile ? [-0.5, 0, 0] : [-0.53, 0, 0];

  return (
    <>
      {/* Caméra en plongée - vue du dessus */}
      <PerspectiveCamera
        makeDefault
        position={cameraPosition}
        fov={cameraFov}
        near={0.1}
        far={1000}
        rotation={cameraRotation}
      />

      <group ref={groupRef} rotation={[0, -Math.PI / 6, 0]}>
        {/* Lumières optimisées pour meilleure lisibilité */}
        <ambientLight intensity={0.7} />

        {/* Key light - principale plus intense */}
        <directionalLight
          position={[12, 18, 10]}
          intensity={2.5}
          color="#ffffff"
          castShadow
          shadow-mapSize={[2048, 2048]}
        />

        {/* Fill light - adoucie */}
        <directionalLight position={[-10, 12, -8]} intensity={1.0} color="#ffd8b0" />

        {/* Rim light - contour plus marqué */}
        <directionalLight position={[0, 10, -12]} intensity={1.3} color="#a0d0ff" />

        {/* Top light - plus diffuse */}
        <pointLight position={[0, 20, 0]} intensity={1.5} color="#ffffff" distance={30} decay={1.3} />

        {/* Accent lights pour profondeur */}
        <pointLight position={[6, 6, 6]} intensity={0.8} color="#ffb8a0" distance={18} />
        <pointLight position={[-6, 6, -6]} intensity={0.8} color="#a0c8ff" distance={18} />


        {/* Sol avec réflexions style verre */}



        {/* 4 LAYERS empilées (0 à 3) - progression selon STEP_CONFIG */}
        {Array.from({ length: 4 }).map((_, layerIdx) => {
          const layerY = layerIdx * layerHeight;
          const layerVisible = isLayerVisible(layerIdx);

          return (
            <group key={`layer-${layerIdx}`} position={[0, layerY, 0]}>

              {/* Grille 2×2 de cubes - tout le layer apparaît ensemble */}
              {Array.from({ length: 2 }).map((_, rowIdx) => (
                <group key={`row-${rowIdx}`}>
                  {Array.from({ length: 2 }).map((_, colIdx) => {
                    const delay = colIdx * 80 + rowIdx * 60;
                    let color = getCubeColor(layerIdx, rowIdx, colIdx);
                    let isHighlighted = false;

                    // Si c'est le dernier layer visible et qu'un produit est sélectionné (sauf Eden qui a une croix)
                    const isEden = selectedProduct === 1;
                    if (layerIdx === 3 && selectedProduct !== null && !isEden) {
                      const productMapping = PRODUCT_COLUMN_MAP[selectedProduct];
                      if (productMapping) {
                        // Vérifier si ce cube correspond au positionnement du produit
                        const productRow = Math.round(productMapping.row);
                        const productCol = Math.round(productMapping.col);

                        if (rowIdx === productRow && colIdx === productCol) {
                          // Ce cube est à la position du produit, changer sa couleur
                          color = products[selectedProduct].color;
                          isHighlighted = true;
                        }
                      }
                    }

                    return (
                      <StackedCube
                        key={`cube-${layerIdx}-${rowIdx}-${colIdx}`}
                        position={[
                          colIdx * cubeSpacing - gridOffset,
                          0,
                          rowIdx * cubeSpacing - gridOffset,
                        ]}
                        color={color}
                        isVisible={layerVisible}
                        delay={delay}
                        isHighlighted={isHighlighted}
                        highlightColor={color}
                        isMobile={isMobile}
                      />
                    );
                  })}
                </group>
              ))}
            </group>
          );
        })}

        {/* COUCHE PRODUIT - Seulement pour Eden (croix) */}
        {selectedProduct === 1 && products[selectedProduct] && (() => {
          const productMapping = PRODUCT_COLUMN_MAP[selectedProduct];
          if (!productMapping) return null;

          const { row, col } = productMapping;
          const productColor = "#11458d";
          const productLayerY = 3 * layerHeight;
          const edenTooltip = "Optimized for Near-Intermediate Vision\nFor older patients with preference for reading & TV";

          return (
            <group position={[0, productLayerY, 0]}>
              <ProductCross
                key={`product-cross`}
                position={[
                  col * cubeSpacing - gridOffset,
                  0,
                  row * cubeSpacing - gridOffset,
                ]}
                color={productColor}
                isVisible={true}
                delay={0}
                tooltipContent={edenTooltip}
                showTooltipAlways={true}
                isMobile={isMobile}
              />

              {/* Éclairage directionnel sur Eden */}
              <spotLight
                position={[
                  col * cubeSpacing - gridOffset,
                  2,
                  row * cubeSpacing - gridOffset
                ]}
                angle={0.8}
                penumbra={0.4}
                intensity={4}
                color={productColor}
                distance={10}
                decay={1.8}
              />
            </group>
          );
        })()}

        {/* Tooltips pour Lucidis et Innovation 2026 - affichés au-dessus du cube correspondant */}
        {selectedProduct !== null && selectedProduct !== 1 && products[selectedProduct] && (() => {
          const productMapping = PRODUCT_COLUMN_MAP[selectedProduct];
          if (!productMapping) return null;

          const productRow = Math.round(productMapping.row);
          const productCol = Math.round(productMapping.col);
          const isLucidis = selectedProduct === 0;
          const isNewProduct = selectedProduct === 2;
          const productLayerY = 3 * layerHeight;

          const lucidisTooltip = isLucidis ? "Pioneer in Fully Refractive technology\nFull Range Vision without disturbances" : null;
          const newProductTooltip = isNewProduct ? "Unique preloaded solution for usability & safety\nOptimized for Distance Vision & Low light conditions\nDiscover more" : null;

          return (
            <group position={[0, productLayerY + (isMobile ? 0.8 : 1.2), 0]}>
              <Html 
                position={[
                  productCol * cubeSpacing - gridOffset,
                  0,
                  productRow * cubeSpacing - gridOffset,
                ]} 
                center
                style={{
                  pointerEvents: isNewProduct ? 'auto' : 'none',
                }}
              >
                <div style={{
                  background: `${products[selectedProduct].color}f2`,
                  color: '#ffffff',
                  padding: isMobile ? '6px 8px' : '8px 12px',
                  borderRadius: '6px',
                  fontSize: isMobile ? '9px' : '11px',
                  fontWeight: '600',
                  lineHeight: '1.3',
                  whiteSpace: 'normal',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                  border: '2px solid rgba(255,255,255,0.2)',
                  pointerEvents: isNewProduct ? 'auto' : 'none',
                  maxWidth: isMobile ? '140px' : '200px',
                  textAlign: 'center',
                }}>
                  {(lucidisTooltip || newProductTooltip).split('\n').map((line, i) => {
                    const isLastLine = i === (lucidisTooltip || newProductTooltip).split('\n').length - 1;
                    const isDiscoverMore = line.toLowerCase().includes('discover more');

                    if (isLastLine && isDiscoverMore && isNewProduct) {
                      return (
                        <div
                          key={i}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = "#/new-product";
                          }}
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            marginTop: '4px',
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.8';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        >
                          {line}
                        </div>
                      );
                    }
                    return <div key={i}>{line}</div>;
                  })}
                </div>
              </Html>
            </group>
          );
        })()}

        {/* AXE 1: Technology (Diffractive / Refractive) - DEVANT avec flèche */}
        <group position={[0, -0.1, gridOffset + 3.5]}>
          {/* Ligne d'axe avec flèche */}
          <mesh position={[0, 0, 0]} rotation={[0, 0, 0]}>
            <boxGeometry args={[cubeSpacing * 1, 0.03, 0.03]} />
            <meshStandardMaterial color="#C41230" metalness={0.6} roughness={0.3} />
          </mesh>

          {/* Flèches aux extrémités */}
          <mesh position={[-gridOffset - 0.15, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <coneGeometry args={[0.08, 0.2, 4]} />
            <meshStandardMaterial color="#C41230" />
          </mesh>
          <mesh position={[gridOffset + 0.15, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
            <coneGeometry args={[0.08, 0.2, 4]} />
            <meshStandardMaterial color="#C41230" />
          </mesh>

          {/* Valeurs de l'axe - au-dessus de l'axe - CLIQUABLES */}
          <AxisLabel
            text="Diffractive"
            position={[-gridOffset - 2, 0.4, 0]}
            isSelected={selectedColumn === "0-0"}
            onClick={(e) => {
              e.stopPropagation();
              onColumnSelect(selectedColumn === "0-0" ? null : "0-0");
            }}
          />
          <AxisLabel
            text="Refractive"
            position={[gridOffset + 2, 0.4, 0]}
            isSelected={selectedColumn === "0-1"}
            onClick={(e) => {
              e.stopPropagation();
              onColumnSelect(selectedColumn === "0-1" ? null : "0-1");
            }}
          />

          {/* Titre de l'axe - sous l'axe */}
          <Html position={[-1.5, -0.6, 1]} center transform={true}>
            <div style={{
              fontSize: "11px",
              fontWeight: 800,
              width: "120px",
              color: "#C41230",
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              padding: "8px 18px",

            }}>
              1. TECHNOLOGY
            </div>
          </Html>
        </group>

        {/* AXE 2: Pricing (Competitive / Premium) - CÔTÉ DROIT avec flèche */}
        <group position={[gridOffset + 4.6, -0.2, 0]}>
          {/* Ligne d'axe avec flèche */}
          <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
            <boxGeometry args={[cubeSpacing * 1, 0.03, 0.03]} />
            <meshStandardMaterial color="#C41230" metalness={0.6} roughness={0.3} />
          </mesh>

          {/* Flèches aux extrémités */}
          <mesh position={[0, 0, -gridOffset - 0.15]} rotation={[-Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.08, 0.2, 4]} />
            <meshStandardMaterial color="#C41230" />
          </mesh>
          <mesh position={[0, 0, gridOffset + 0.15]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.08, 0.2, 4]} />
            <meshStandardMaterial color="#C41230" />
          </mesh>

          {/* Valeurs de l'axe - au-dessus de l'axe - CLIQUABLES */}
          <AxisLabel
            text="Competitive"
            position={[
              (selectedColumn === "0-0")
                ? 5  // Décalé plus loin quand sélectionné
                : 0,
              0.4,
              (selectedColumn === "0-0")
                ? -gridOffset - 6  // Décalé plus loin quand sélectionné
                : -gridOffset - 2
            ]}
            isSelected={selectedColumn === "0-0"}
            onClick={(e) => {
              e.stopPropagation();
              onColumnSelect(selectedColumn === "0-0" ? null : "0-0");
            }}
          />
          <AxisLabel
            text="Premium"
            position={[0, 0.4, gridOffset + 2]}
            isSelected={selectedColumn === "1-0" || selectedColumn === "1-1"}
            onClick={(e) => {
              e.stopPropagation();
              onColumnSelect(selectedColumn === "1-1" ? null : "1-1");
            }}
          />

          {/* Titre de l'axe - sous l'axe */}
          <Html position={[2, 0.5, 0]} rotation={[0, 0, Math.PI / 4]} center transform={true} >
            <div style={{
              fontSize: "11px",
              fontWeight: 800,
              width: "120px",
              color: "#C41230",
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              padding: "8px 18px",

            }}>
              2. PRICING
            </div>
          </Html>
        </group>

      </group>

      {/* AXE 3: Visual Performance - VERTICAL (hauteur) - Fixe sur le côté - VERSION PLATE */}
      <group position={[-6.5, 0, -4]}>
        {/* Ligne d'axe verticale - plate */}
        <mesh position={[0, layerHeight * 1.5, 0]}>
          <boxGeometry args={[0.06, layerHeight * 4, 0.01]} />
          <meshBasicMaterial color="#C41230" transparent opacity={0.9} />
        </mesh>

        {/* Flèche en haut - plate */}
        <mesh position={[0, layerHeight * 3.2 + 0.15, 0]}>
          <coneGeometry args={[0.12, 0.3, 4]} />
          <meshBasicMaterial color="#C41230" transparent opacity={0.9} />
        </mesh>

        {/* Marqueurs de niveaux sur l'axe - plats */}
        {[0, 1, 2, 3].map((level) => (
          <mesh key={`marker-${level}`} position={[0, level * layerHeight, 0]}>
            <boxGeometry args={[0.15, 0.03, 0.01]} />
            <meshBasicMaterial color="#C41230" transparent opacity={0.9} />
          </mesh>
        ))}

        {/* Titre de l'axe - vertical à gauche */}
        <Html position={[-0.6, layerHeight * 1.5, 0]} center transform={true}>
          <div style={{
            fontSize: "12px",
            fontWeight: 800,
            color: "#C41230",
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            padding: "10px 20px",
            transform: "rotate(-90deg)",
            transformOrigin: "center",
            whiteSpace: "nowrap",
          }}>
            3. Range of Vision
          </div>
        </Html>
      </group>
    </>
  );
}

/**
 * Canvas wrapper
 */
export function ComparatifCanvas({
  gridData = [],
  stepIdx = 0,
  products = [],
  selectedProduct = null,
  selectedColumn = null,
  onColumnSelect = () => { },
  isMobile = false,
}) {
  return (
    <Canvas
      style={{
        width: "100%",
        height: "100%",
        background: "#ffffff",
      }}
      dpr={[1, 2]}
      shadows
      gl={{
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 1.3,
      }}
    >
      <Comparatif3D
        gridData={gridData}
        stepIdx={stepIdx}
        products={products}
        selectedProduct={selectedProduct}
        selectedColumn={selectedColumn}
        onColumnSelect={onColumnSelect}
        isMobile={isMobile}
      />
    </Canvas>
  );
}
